import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { APP_VERSION, CHANGELOG_ENTRIES, type ChangelogEntry } from '@uniticket/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

// Accent dot per CHANGELOG section heading (falls back to the brand pink).
const SECTION_DOT: Record<string, string> = {
  Added: 'bg-emerald-500',
  Fixed: 'bg-amber-500',
  Changed: 'bg-sky-500',
  Removed: 'bg-rose-500',
  'Database / Ops': 'bg-slate-400',
};

/** Render the limited inline markdown we allow in CHANGELOG bullets: **bold** and `code`. */
function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-slate-700">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.8em] text-brand-purple">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

function EntryBody({ entry, showHeading, locale }: { entry: ChangelogEntry; showHeading: boolean; locale: string }) {
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(entry.date),
  );
  return (
    <div className="space-y-3">
      {showHeading && (
        <div className="flex items-baseline gap-2 border-b border-slate-100 pb-1">
          <span className="text-sm font-bold text-[#4f4a54]">v{entry.version}</span>
          <span className="text-xs text-slate-400">{date}</span>
        </div>
      )}
      {entry.sections.map((section, si) => (
        <div key={si}>
          {section.title && (
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${SECTION_DOT[section.title] ?? 'bg-brand-rose'}`} />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{section.title}</span>
            </div>
          )}
          <ul className="space-y-1.5">
            {section.items.map((item, ii) => (
              <li key={ii} className="flex gap-2 text-sm leading-relaxed text-slate-600">
                <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gradient-to-r from-brand-rose to-brand-orange" />
                <span>{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

interface ChangelogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "What's new" modal — shows the changelog for the CURRENT app version (APP_VERSION), with an
 * in-app "view full changelog" expansion. Reads the generated CHANGELOG_ENTRIES (single source:
 * CHANGELOG.md). Controlled via open/onOpenChange so it can be triggered from inside the user
 * menu's Radix dropdown without the two focus traps colliding.
 */
export function ChangelogDialog({ open, onOpenChange }: ChangelogDialogProps) {
  const { t, i18n } = useTranslation('common');
  const [showAll, setShowAll] = useState(false);

  const current: ChangelogEntry | undefined =
    CHANGELOG_ENTRIES.find((e) => e.version === APP_VERSION) ?? CHANGELOG_ENTRIES[0];

  const formattedDate = current
    ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(
        new Date(current.date),
      )
    : '';

  const showEnglishNote = !i18n.language.startsWith('en');
  const visibleEntries = showAll ? CHANGELOG_ENTRIES : current ? [current] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="max-w-[min(34rem,calc(100vw-1.5rem))] border-white/40 bg-white/95 shadow-2xl backdrop-blur-xl"
      >
        {/* Brand gradient band flush to the top edge */}
        <div className="-mx-6 -mt-6 h-1 rounded-t-xl bg-gradient-to-r from-[#84257a] via-[#ed125f] to-[#f47b3b]" />

        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-[#4f4a54]">
            <Sparkles className="h-4 w-4 text-brand-rose" />
            {t('changelog.title')}
            {current && (
              <span className="rounded-full bg-gradient-to-r from-[#84257a] to-[#ed125f] px-2 py-0.5 text-xs font-semibold text-white">
                v{current.version}
              </span>
            )}
          </DialogTitle>
          {current && <DialogDescription>{t('changelog.releasedOn', { date: formattedDate })}</DialogDescription>}
        </DialogHeader>

        {showEnglishNote && <p className="-mt-2 text-[11px] italic text-slate-400">{t('changelog.englishNote')}</p>}

        <div className="brand-scrollbar -mr-2 max-h-[60vh] space-y-5 overflow-y-auto pr-2">
          {visibleEntries.length === 0 ? (
            <p className="text-sm text-slate-500">{t('changelog.empty')}</p>
          ) : (
            visibleEntries.map((entry) => (
              <EntryBody
                key={entry.version}
                entry={entry}
                showHeading={showAll}
                locale={i18n.language}
              />
            ))
          )}
        </div>

        {CHANGELOG_ENTRIES.length > 1 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="self-start text-xs font-medium text-brand-rose transition-colors hover:text-brand-purple"
          >
            {showAll ? t('changelog.showLess') : `${t('changelog.viewFull')} →`}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
